import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Footer } from "../components/Footer";
import { CourierSystems } from "../components/CourierSystems";
import { CourierSchematic } from "../components/CourierSchematic";
import { FactionGuide } from "../components/FactionGuide";
import { Reveal } from "../components/Reveal";
import { toast } from "../components/ui/sonner";
import { artAssets } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const factions = [
  "APEX-COURIER / AETHER-GRID",
  "BLACK-OPS NET",
  "SCRAPPER",
  "SAFE ZONE ARMY",
  "WASTELAND RUNNER",
  "UNAFFILIATED",
];

const initialForm = {
  operator_name: "",
  email: "",
  faction: factions[0],
  message: "",
};

export default function Enlist() {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setStatus("");
    try {
      const { data } = await axios.post(`${API}/enlist`, form);
      setStatus(data.message);
      toast.success(data.message);
      setForm(initialForm);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message =
        typeof detail === "string" ? detail : (Array.isArray(detail) && detail[0]?.msg) || "ENLISTMENT FAILED. CHECK YOUR SIGNAL.";
      setStatus(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="enlist-page" data-testid="enlist-page">
      <section className="enlist-hero">
        <Link to="/" className="back-link" data-testid="enlist-back-link">
          <ArrowLeft size={16} /> RETURN TO WORLD
        </Link>
        <Reveal>
          <p className="eyebrow">APEX-COURIER MK-VII / OPERATOR INTAKE</p>
          <h1>JOIN THE ELITE FLEET. SECURE THE FUTURE.</h1>
          <p>
            Submit your operator signal for review by Aether-Grid Logistics. Applications are stored securely in the cloud archive.
          </p>
        </Reveal>
      </section>

      <section className="enlist-grid" data-testid="enlist-form-section">
        <Reveal className="enlist-visual">
          <img src={artAssets.recruitmentBanner} alt="Apex-Courier MK-VII recruitment banner" data-testid="enlist-banner-image" />
        </Reveal>
        <Reveal className="enlist-form-shell" delay={0.12}>
          <form className="enlist-form" onSubmit={submit} data-testid="enlistment-form">
            <div className="form-field">
              <label htmlFor="operator-name">OPERATOR NAME</label>
              <input
                id="operator-name"
                value={form.operator_name}
                onChange={(event) => updateField("operator_name", event.target.value)}
                minLength={2}
                maxLength={80}
                required
                disabled={loading}
                data-testid="enlist-name-input"
              />
            </div>
            <div className="form-field">
              <label htmlFor="operator-email">CONTACT FREQUENCY / EMAIL</label>
              <input
                id="operator-email"
                type="email"
                value={form.email}
                onChange={(event) => updateField("email", event.target.value)}
                required
                disabled={loading}
                data-testid="enlist-email-input"
              />
            </div>
            <div className="form-field">
              <label htmlFor="operator-faction">FACTION PREFERENCE</label>
              <select
                id="operator-faction"
                value={form.faction}
                onChange={(event) => updateField("faction", event.target.value)}
                disabled={loading}
                data-testid="enlist-faction-select"
              >
                {factions.map((faction) => (
                  <option value={faction} key={faction}>{faction}</option>
                ))}
              </select>
            </div>
            <div className="form-field form-field-full">
              <label htmlFor="operator-message">APPLICATION MESSAGE</label>
              <textarea
                id="operator-message"
                value={form.message}
                onChange={(event) => updateField("message", event.target.value)}
                minLength={10}
                maxLength={1000}
                rows={6}
                required
                disabled={loading}
                data-testid="enlist-message-input"
              />
            </div>
            <button type="submit" disabled={loading} data-testid="enlist-submit-button">
              {loading ? "TRANSMITTING" : "[ ENLIST ]"}
            </button>
            <p className="terminal-status" aria-live="polite" data-testid="enlist-status-message">
              {status || "Operator records are encrypted into the cloud archive."}
            </p>
          </form>
        </Reveal>
      </section>
      <FactionGuide activeFaction={form.faction} />
      <CourierSchematic />
      <CourierSystems />
      <Footer />
    </main>
  );
}
